import { useEffect, useRef } from "react";

/**
 * Subscribes to live user-scoped events (offers + trade-ins).
 * onEvent receives a JSON payload with at least: { type, listing_id, ... }
 * — type is one of: offer_created|offer_countered|offer_accepted|offer_rejected|offer_withdrawn
 *                   tradein_created|tradein_countered|tradein_accepted|tradein_rejected|tradein_withdrawn
 */
export default function useUserSocket(userId, onEvent) {
    const wsRef = useRef(null);
    const reconnectRef = useRef(null);
    const onEventRef = useRef(onEvent);

    useEffect(() => {
        onEventRef.current = onEvent;
    }, [onEvent]);

    useEffect(() => {
        if (!userId) return undefined;

        const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
        const wsUrl = backendUrl.replace(/^http/, "ws") + `/api/ws/user/${userId}`;

        let closed = false;
        let pingInterval = null;

        const connect = () => {
            if (closed) return;
            try {
                const ws = new WebSocket(wsUrl);
                wsRef.current = ws;

                ws.onmessage = (e) => {
                    if (e.data === "pong") return;
                    try {
                        const data = JSON.parse(e.data);
                        onEventRef.current?.(data);
                    } catch {
                        /* ignore non-JSON */
                    }
                };

                ws.onopen = () => {
                    pingInterval = setInterval(() => {
                        if (ws.readyState === WebSocket.OPEN) ws.send("ping");
                    }, 25000);
                };

                ws.onclose = () => {
                    if (pingInterval) clearInterval(pingInterval);
                    if (!closed) {
                        reconnectRef.current = setTimeout(connect, 2000);
                    }
                };

                ws.onerror = () => {
                    try { ws.close(); } catch { /* ignore */ }
                };
            } catch (e) {
                console.error("User WS connect failed", e);
                reconnectRef.current = setTimeout(connect, 2000);
            }
        };

        connect();

        return () => {
            closed = true;
            if (pingInterval) clearInterval(pingInterval);
            if (reconnectRef.current) clearTimeout(reconnectRef.current);
            if (wsRef.current) {
                try { wsRef.current.close(); } catch { /* ignore */ }
            }
        };
    }, [userId]);
}
